import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { addDays, getDay } from "date-fns";
import { capitalize } from "lodash";
import { DrawerAction } from "pages/FlowBuilderv2/Drawer/drawer.fixtures";
import { BranchEdgeData, EdgeData } from "pages/FlowBuilderv2/Edges/EdgeData";
import {
  NodeType,
  EdgeType,
  nodeTypesNotConnectableByJumpTo,
} from "pages/FlowBuilderv2/FlowEditor";
import { getLayoutedNodes } from "pages/FlowBuilderv2/layout.helper";
import {
  Branch,
  BranchType,
  EventType,
  LogicRelation,
  MessageNodeData,
  MultisplitBranch,
  NodeData,
  TimeType,
  TimeWindowTypes,
} from "pages/FlowBuilderv2/Nodes/NodeData";
import { WorkflowStatus } from "pages/WorkflowTablev2/WorkflowTablev2";
import { OnboardingAction } from "pages/Onboardingv2/OnboardingSandbox";
import { PushBuilderData } from "pages/PushBuilder/PushBuilderContent";
import {
  applyNodeChanges,
  Edge,
  getIncomers,
  getOutgoers,
  Node,
  NodeChange,
} from "reactflow";
import { MessageType, ProviderType } from "types/Workflow";
import getClosestNextAndPrevious from "utils/getClosestNextAndPrevious";
import { v4 as uuid } from "uuid";
import { Attribute, AttributeType } from "pages/PeopleSettings/PeopleSettings";

export enum SegmentsSettingsType {
  ALL_CUSTOMERS = "allCustomers",
  CONDITIONAL = "conditional",
}

export interface AllCustomersSegmentsSettings {
  type: SegmentsSettingsType.ALL_CUSTOMERS;
}

export enum QueryType {
  ALL = "all",
  ANY = "any",
}

export enum MessageFromWorkflow {
  ANY = "ANY",
  WITH_TAG = "WITH_TAG",
}

export enum MessageGeneralComparison {
  HAS = "has",
  HAS_NOT = "has not",
}

export enum MessageEmailEventCondition {
  RECEIVED = "received",
  OPENED = "opened",
  CLICKED = "clicked",
}

export enum MessagePushEventCondition {
  RECEIVED = "received",
  OPENED = "opened",
}

export enum MessageSMSEventCondition {
  RECEIVED = "received",
  CLICK_LINK = "click_link",
}

export enum MessageInAPPEventCondition {
  RECEIVED = "received",
  OPENED = "opened",
}

export enum QueryStatementType {
  ATTRIBUTE = "Attribute",
  SEGMENT = "Segment",
  EVENT = "Event",
  EMAIL = "Email",
  PUSH = "Push",
  SMS = "SMS",
  IN_APP = "In-app message",
}

export const messageEventsCorelationWaitUntil: Record<
  | ProviderType.EMAIL_MESSAGE
  | ProviderType.SMS_MESSAGE
  | ProviderType.PUSH_MESSAGE
  | ProviderType.IN_APP_MESSAGE,
  {
    key:
      | MessageEmailEventCondition
      | MessageSMSEventCondition
      | MessagePushEventCondition
      | MessageInAPPEventCondition;
    title: string;
  }[]
> = {
  [ProviderType.EMAIL_MESSAGE]: Object.values(MessageEmailEventCondition).map(
    (el) => ({
      key: el,
      title: "is " + el,
    })
  ),
  [ProviderType.SMS_MESSAGE]: [
    {
      key: MessageSMSEventCondition.RECEIVED,
      title: "is " + MessageSMSEventCondition.RECEIVED,
    },
    {
      key: MessageSMSEventCondition.CLICK_LINK,
      title: "sms link is clicked",
    },
  ],
  [ProviderType.PUSH_MESSAGE]: Object.values(MessagePushEventCondition).map(
    (el) => ({
      key: el,
      title: "is " + el,
    })
  ),
  [ProviderType.IN_APP_MESSAGE]: Object.values(MessageInAPPEventCondition).map(
    (el) => ({
      key: el,
      title: "is " + el,
    })
  ),
};

export const WaitUntilMessageProviderCorelation: any = {
  [ProviderType.EMAIL_MESSAGE]: QueryStatementType.EMAIL.toLowerCase(),
  [ProviderType.SMS_MESSAGE]: QueryStatementType.SMS.toLowerCase(),
  [ProviderType.PUSH_MESSAGE]: QueryStatementType.PUSH.toLowerCase(),
  [ProviderType.IN_APP_MESSAGE]: QueryStatementType.IN_APP.toLowerCase(),
};

export type MessageEventTypes =
  | QueryStatementType.EMAIL
  | QueryStatementType.PUSH
  | QueryStatementType.SMS
  | QueryStatementType.IN_APP;

export interface GeneralSelectedType {
  key: string;
  title: string;
}

export interface MessageEventQuery {
  type: MessageEventTypes;
  from: GeneralSelectedType;
  eventCondition:
    | MessageEmailEventCondition
    | MessagePushEventCondition
    | MessageSMSEventCondition
    | MessageInAPPEventCondition;
  happenCondition: MessageGeneralComparison;
  fromSpecificMessage: GeneralSelectedType;
  tag?: string;
  time?: {
    comparisonType:
      | ComparisonType.BEFORE
      | ComparisonType.AFTER
      | ComparisonType.DURING;
    timeAfter?: string;
    timeBefore?: string;
    dateComparisonType: DateComparisonType;
  };
}

export enum ComparisonType {
  EQUALS = "is equal to",
  NOT_EQUALS = "is not equal to",
  OBJECT_KEY = "key",
  DURING = "during",
  ARRAY_LENGTH_GREATER = "length is greater than",
  ARRAY_LENGTH_LESS = "length is less than",
  ARRAY_LENGTH_EQUAL = "length is equal to",
  EXIST = "exist",
  NOT_EXIST = "not exist",
  GREATER = "is greater than",
  LESS = "is less than",
  CONTAINS = "contains",
  NOT_CONTAINS = "does not contain",
  AFTER = "after",
  BEFORE = "before",
  BOOL_EQUALS = "is equal to",
  BOOL_NOT_EQUALS = "is not equal to",
}

export enum ObjectKeyComparisonType {
  KEY_EXIST = "exist",
  KEY_NOT_EXIST = "not exist",
  KEY_VALUE_EQUAL_TO = "equal to",
  KEY_VALUE_NOT_EQUAL_TO = "not equal to",
}

export enum StatementValueType {
  STRING = "String",
  NUMBER = "Number",
  BOOLEAN = "Boolean",
  EMAIL = "Email",
  DATE = "Date",
  DATE_TIME = "DateTime",
  ARRAY = "Array",
  OBJECT = "Object",
}

export function valueTypeToAttributeType(
  valueType: StatementValueType,
  possibleAttributeTypes: AttributeType[]
): AttributeType {
  const attribute = possibleAttributeTypes.find(
    (attr) => attr.name == valueType.toString()
  );
  if (!attribute)
    console.warn(
      `Attribute not found, possible attribute types: ${JSON.stringify(
        possibleAttributeTypes,
        null,
        2
      )}, valueType: ${valueType}`
    );
  else
    console.log(
      `Attribute found, possible attribute types: ${JSON.stringify(
        possibleAttributeTypes,
        null,
        2
      )}, valueType: ${valueType}`
    );
  return attribute!;
}

export const attributeTypeToStatementValueTypeMap: Record<
  string,
  StatementValueType
> = {
  String: StatementValueType.STRING,
  Number: StatementValueType.NUMBER,
  Boolean: StatementValueType.BOOLEAN,
  Email: StatementValueType.EMAIL,
  Date: StatementValueType.DATE,
  DateTime: StatementValueType.DATE_TIME,
  Array: StatementValueType.ARRAY,
  Object: StatementValueType.OBJECT,
};

export const valueTypeToComparisonTypesMap: Record<
  StatementValueType,
  ComparisonType[]
> = {
  [StatementValueType.STRING]: [
    ComparisonType.EQUALS,
    ComparisonType.NOT_EQUALS,
    ComparisonType.CONTAINS,
    ComparisonType.NOT_CONTAINS,
    ComparisonType.EXIST,
    ComparisonType.NOT_EXIST,
  ],
  [StatementValueType.NUMBER]: [
    ComparisonType.GREATER,
    ComparisonType.EQUALS,
    ComparisonType.NOT_EQUALS,
    ComparisonType.LESS,
    ComparisonType.EXIST,
    ComparisonType.NOT_EXIST,
  ],
  [StatementValueType.BOOLEAN]: [
    ComparisonType.BOOL_EQUALS,
    ComparisonType.BOOL_NOT_EQUALS,
    ComparisonType.EXIST,
    ComparisonType.NOT_EXIST,
  ],
  [StatementValueType.EMAIL]: [
    ComparisonType.EQUALS,
    ComparisonType.NOT_EQUALS,
    ComparisonType.CONTAINS,
    ComparisonType.NOT_CONTAINS,
    ComparisonType.EXIST,
    ComparisonType.NOT_EXIST,
  ],
  [StatementValueType.DATE]: [
    ComparisonType.BEFORE,
    ComparisonType.AFTER,
    ComparisonType.DURING,
    ComparisonType.EXIST,
    ComparisonType.NOT_EXIST,
  ],
  [StatementValueType.DATE_TIME]: [
    ComparisonType.BEFORE,
    ComparisonType.AFTER,
    ComparisonType.DURING,
    ComparisonType.EXIST,
    ComparisonType.NOT_EXIST,
  ],
  [StatementValueType.ARRAY]: [
    ComparisonType.ARRAY_LENGTH_GREATER,
    ComparisonType.ARRAY_LENGTH_LESS,
    ComparisonType.ARRAY_LENGTH_EQUAL,
    ComparisonType.CONTAINS,
    ComparisonType.NOT_CONTAINS,
    ComparisonType.EXIST,
    ComparisonType.NOT_EXIST,
  ],
  [StatementValueType.OBJECT]: [
    ComparisonType.OBJECT_KEY,
    ComparisonType.EXIST,
    ComparisonType.NOT_EXIST,
  ],
};

export enum DateComparisonType {
  ABSOLUTE = "absolute",
  RELATIVE = "relative",
}

export interface AttributeQueryStatement {
  type: QueryStatementType.ATTRIBUTE;
  key: string;
  valueType?: StatementValueType;
  attribute?: Attribute;
  comparisonType: ComparisonType;
  subComparisonType: ObjectKeyComparisonType;
  subComparisonValue: string;
  value: string;
  dateComparisonType: DateComparisonType;
}

export enum PerformedType {
  HasPerformed = "has performed",
  HasNotPerformed = "has not performed",
}

export interface EventQueryAdditionalProperty {
  key: string;
  valueType: StatementValueType;
  comparisonType: ComparisonType;
  subComparisonType: ObjectKeyComparisonType;
  subComparisonValue: string;
  value: string;
}

export interface EventQueryAdditionalProperties {
  comparison: QueryType;
  properties: EventQueryAdditionalProperty[];
}

export interface EventQueryStatement {
  type: QueryStatementType.EVENT;
  eventName: string;
  comparisonType: PerformedType;
  additionalProperties: EventQueryAdditionalProperties;
  value: number;
  time?: {
    comparisonType:
      | ComparisonType.BEFORE
      | ComparisonType.AFTER
      | ComparisonType.DURING;
    timeAfter?: string;
    timeBefore?: string;
    dateComparisonType: DateComparisonType;
  };
}

export interface SegmentQueryStatement {
  type: QueryStatementType.SEGMENT;
  segmentId: string;
}

export type QueryStatement =
  | AttributeQueryStatement
  | SegmentQueryStatement
  | EventQueryStatement
  | MessageEventQuery
  | Query;

export interface Query {
  type: QueryType;
  isSubBuilderChild?: boolean;
  statements: QueryStatement[];
}

export interface ConditionalSegmentsSettings {
  type: SegmentsSettingsType.CONDITIONAL;
  query: Query;
}

export type SegmentsSettings =
  | AllCustomersSegmentsSettings
  | ConditionalSegmentsSettings;

export enum WorkflowType {
  DYNAMIC = "dynamic",
  STATIC = "static",
}

export enum NodeAction {
  SWAP = "swap",
}

export interface DrawerDragAction {
  type: DrawerAction;
}

export interface SwapDragAction {
  type: NodeAction.SWAP;
  nodeId: string;
}

export interface OnboardingDragAction {
  type: OnboardingAction;
  targetId?: string;
}

export type DragAction =
  | DrawerDragAction
  | SwapDragAction
  | OnboardingDragAction;

export const enum ConnectionStatus {
  Disabled,
  ShowPreview,
  Connecting,
  Reconnection,
  Error,
  Connected,
}

interface DevModeStatePayload {
  status?: ConnectionStatus;
  contactInNode?: string;
  arrowPreSelectNode?: string;
  availableNodeToJump?: string[];
  requireMovementToStart?: string;
}

export enum EntryTiming {
  WhenPublished = "WhenPublished",
  SpecificTime = "SpecificTime",
}

export enum EntryTimingFrequency {
  Once = "Once",
  Daily = "Daily",
  Weekly = "Weekly",
  Monthly = "Monthly",
}

export enum RecurrenceEndsOption {
  Never = "Never",
  After = "After",
  SpecificDate = "SpecificDate",
}

export interface EntryTimingRecurrence {
  repeatEvery: number;
  endsOn: RecurrenceEndsOption;
  endAdditionalValue?: number | string; // string as Date (Ends after number occurnecs or on Specific string Date )
  weeklyOn: number[]; // Day of week number
}

export interface EntryTimingSpecificTime {
  startDate: string;
  frequency: EntryTimingFrequency;
  recurrence: EntryTimingRecurrence;
  userLocalTimeZone: boolean;
}

export enum WorkflowEnrollmentType {
  CurrentAndFutureUsers = "CurrentAndFutureUsers",
  OnlyCurrent = "OnlyCurrent",
  OnlyFuture = "OnlyFuture",
}

export interface EntryTimingSettings {
  type: EntryTiming;
  time?: EntryTimingSpecificTime;
}

export interface WorkflowEntrySettings {
  entryTiming: EntryTimingSettings;
  enrollmentType: WorkflowEnrollmentType;
}

export enum WorkflowSettingsQuietFallbackBehavior {
  NextAvailableTime = "NextAvailableTime",
  Abort = "Abort",
}

export interface WorkflowSettingsQuietHours {
  enabled: boolean;
  mode?: "quietHours" | "activeHours";
  startTime: string;
  endTime: string;
  timezone?: string;
  daysOfWeek?: number[];
  skipHolidays?: boolean;
  fallbackBehavior: WorkflowSettingsQuietFallbackBehavior;
}

export enum MaxOptions {
  Ten = "10",
  Fifty = "50",
  OneHundred = "100",
  FiveHundred = "500",
  OneThousand = "1000",
  FiveThousand = "5000",
  TenThousand = "10000",
  TwentyFiveThousand = "25000",
  FiftyThousand = "50000",
  OneHundredThousand = "100000",
  TwoHundredFiftyThousand = "250000",
  FiveHundredThousand = "500000",
}

export interface WorkflowSettingsMaxUserEntries {
  enabled: boolean;
  maxEntries: MaxOptions;
  limitOnEverySchedule: boolean;
}

export interface WorkflowSettingsMaxMessageSends {
  enabled: boolean;
  maxUsersReceive?: MaxOptions;
  maxSendRate?: MaxOptions;
}

export interface WorkflowSettingsEnableFrequencyCapping {
  enabled: boolean;
}

export enum WorkflowSettingsConversionTrackingTimeLimitUnit {
  Days = "Days",
}

export interface WorkflowSettingsConversionTrackingTimeLimit {
  unit: WorkflowSettingsConversionTrackingTimeLimitUnit;
  value: number;
}

export interface WorkflowSettingsConversionTracking {
  enabled: boolean;
  events: string[];
  timeLimit: WorkflowSettingsConversionTrackingTimeLimit;
}

export interface WorkflowSettingsStrictLiquidChecking {
  enabled: boolean;
}

export type LeadStatsResourceType =
  | "lead"
  | "policy"
  | "policy_account"
  | "policy_item"
  | "document";

export type LeadStatsIdentityKey = "lead_id" | "email" | "phone";

export type LeadStatsConditionOperator =
  | "and"
  | "or"
  | "eq"
  | "neq"
  | "in"
  | "not_in"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "contains"
  | "not_contains"
  | "exists"
  | "not_exists"
  | "changed_to"
  | "changed_from"
  | "changed"
  | "previously_reached";

export type LeadStatsConditionValueType =
  | "string"
  | "number"
  | "date"
  | "datetime"
  | "boolean"
  | "array"
  | "enum";

export type LeadStatsConditionNode =
  | {
      op: "and" | "or";
      children: LeadStatsConditionNode[];
    }
  | {
      op: Exclude<LeadStatsConditionOperator, "and" | "or">;
      field: string;
      value?: unknown;
      valueType: LeadStatsConditionValueType;
      fieldDefinitionId?: string;
      fieldDisplayName?: string;
      companyId?: string;
      resourceType?: LeadStatsResourceType;
    };

export interface LeadStatsWorkflowTrigger {
  enabled: boolean;
  source: "leadstats";
  triggerMode?: "event" | "date";
  triggerEvent: string;
  resourceType: LeadStatsResourceType;
  identityKeys: LeadStatsIdentityKey[];
  conditionTree: LeadStatsConditionNode;
  dateTrigger?: {
    enabled: boolean;
    dateField: string;
    relation: "before" | "after";
    amount: number;
    unit: "days" | "weeks" | "months";
    timeOfDay: string;
    timezone: string;
  };
  triggerDelay?: {
    enabled: boolean;
    amount: number;
    unit: "minutes" | "hours" | "days" | "business_days";
    businessHoursOnly?: boolean;
    timezone?: string;
    businessHours?: {
      daysOfWeek?: number[];
      startTime?: string;
      endTime?: string;
    };
    recheckBeforeStart?: boolean;
  };
}

export type WorkflowEntryTriggerType =
  | "audience_schedule"
  | "leadstats_event"
  | "webhook";

export interface WorkflowWebhookTrigger {
  enabled: boolean;
  source: "webhook";
  identityPaths: string[];
  samplePayload?: Record<string, any>;
  endpointKey?: string;
  endpointUrl?: string;
  rateLimit?: {
    enabled: boolean;
    maxPerMinute?: number;
    maxPerHour?: number;
  };
}

export interface WorkflowSettings {
  entryTriggerType?: WorkflowEntryTriggerType;
  tags: string[];
  quietHours: WorkflowSettingsQuietHours;
  maxEntries: WorkflowSettingsMaxUserEntries;
  maxMessageSends: WorkflowSettingsMaxMessageSends;
  frequencyCapping: WorkflowSettingsEnableFrequencyCapping;
  conversionTracking: WorkflowSettingsConversionTracking;
  strictLiquidChecking: WorkflowSettingsStrictLiquidChecking;
  leadstatsTrigger?: LeadStatsWorkflowTrigger;
  webhookTrigger?: WorkflowWebhookTrigger;
}

export type WorkflowPreflightIssueSeverity =
  | "error"
  | "warning"
  | "info"
  | string;

export interface WorkflowPreflightIssue {
  stepId?: string;
  nodeId?: string;
  stepType?: string;
  code: string;
  message: string;
  field?: string;
  severity?: WorkflowPreflightIssueSeverity;
}

export interface WorkflowPreflightResult {
  ok: boolean;
  issueCount: number;
  issues: WorkflowPreflightIssue[];
}

export interface TemplateInlineEditor {
  selectedNode: Node<NodeData, string | undefined>;
  templateId?: string;
  type: MessageType;
  needsCallbackUpdate?: {
    id: number | string;
    name: string;
    data: PushBuilderData;
  };
}

export interface FlowBuilderState {
  flowId: string;
  flowName: string;
  nodes: Node<NodeData>[];
  edges: Edge<EdgeData>[];
  isDragging: boolean;
  dragAction?: DragAction;
  stepperIndex: 0 | 1 | 2 | 3;
  segments: SegmentsSettings;
  workflowType: WorkflowType;
  isViewMode: boolean;
  flowStatus: WorkflowStatus;
  templateInlineCreation?: TemplateInlineEditor;
  showSegmentsErrors: boolean;
  isOnboarding: boolean;
  isOnboardingWaitUntilTooltipVisible: boolean;
  isOnboardingWaitUntilTimeSettingTooltipVisible: boolean;
  requireSaveEmit: boolean;
  sidePanelErrors: Record<string, any>;
  jumpToTargettingNode?: string;
  isDrawerDisabled: boolean;
  segmentQueryErrors: Record<string, any>;
  devModeState: DevModeStatePayload;
  workflowEntrySettings: WorkflowEntrySettings;
  workflowSettings: WorkflowSettings;
  availableTags: string[];
  isStarting: boolean;
  activationPreflight: WorkflowPreflightResult;
  dragDeleteHistory: {
    nodes: Node<NodeData>[];
    edges: Edge<EdgeData>[];
  }[];
}

const startNodeUUID = uuid();
const nextNodeUUID = uuid();

const initialNodes: Node<NodeData>[] = [
  {
    id: startNodeUUID,
    type: NodeType.START,
    data: {},
    position: { x: 0, y: 0 },
  },
  {
    id: nextNodeUUID,
    type: NodeType.EMPTY,
    data: {},
    position: { x: 0, y: 0 },
  },
];
const initialEdges: Edge<EdgeData>[] = [
  {
    id: `e${startNodeUUID}-${nextNodeUUID}`,
    type: EdgeType.PRIMARY,
    source: startNodeUUID,
    target: nextNodeUUID,
  },
];

const defaultDevMode: DevModeStatePayload = {
  status: ConnectionStatus.Disabled,
  contactInNode: undefined,
  availableNodeToJump: undefined,
  arrowPreSelectNode: undefined,
  requireMovementToStart: undefined,
};

export const defaultWorkflowEntrySettings = {
  entryTiming: {
    type: EntryTiming.WhenPublished,
    time: undefined,
  },
  enrollmentType: WorkflowEnrollmentType.CurrentAndFutureUsers,
};

export const defaultWorkflowSettings: WorkflowSettings = {
  entryTriggerType: "leadstats_event",
  tags: [],
  maxEntries: {
    enabled: false,
    limitOnEverySchedule: false,
    maxEntries: MaxOptions.FiveHundredThousand,
  },
  quietHours: {
    enabled: true,
    mode: "activeHours",
    startTime: "08:00",
    endTime: "18:00",
    daysOfWeek: [1, 2, 3, 4, 5],
    skipHolidays: true,
    fallbackBehavior: WorkflowSettingsQuietFallbackBehavior.NextAvailableTime,
  },
  maxMessageSends: {
    enabled: false,
    maxSendRate: undefined,
    maxUsersReceive: undefined,
  },
  frequencyCapping: {
    enabled: false,
  },
  conversionTracking: {
    enabled: false,
    events: [],
    timeLimit: {
      unit: WorkflowSettingsConversionTrackingTimeLimitUnit.Days,
      value: 3,
    },
  },
  strictLiquidChecking: { enabled: false },
  leadstatsTrigger: {
    enabled: true,
    source: "leadstats",
    triggerMode: "event",
    triggerEvent: "lead_status_changed",
    resourceType: "lead",
    identityKeys: ["lead_id", "email", "phone"],
    conditionTree: {
      op: "and",
      children: [
        {
          op: "changed_to",
          field: "status_id",
          value: "",
          valueType: "enum",
        },
      ],
    },
    dateTrigger: {
      enabled: false,
      dateField: "policy_expiration_date",
      relation: "before",
      amount: 2,
      unit: "weeks",
      timeOfDay: "09:00",
      timezone: "UTC",
    },
    triggerDelay: {
      enabled: false,
      amount: 15,
      unit: "minutes",
    },
  },
  webhookTrigger: {
    enabled: false,
    source: "webhook",
    identityPaths: ["lead_id", "email"],
    rateLimit: {
      enabled: true,
      maxPerMinute: 60,
      maxPerHour: 1000,
    },
    samplePayload: {
      lead_id: "lead_123",
      email: "customer@example.com",
    },
  },
};

const getWorkflowEntryTriggerType = (
  workflowSettings?: Partial<WorkflowSettings>
): WorkflowEntryTriggerType => {
  if (workflowSettings?.entryTriggerType) {
    return workflowSettings.entryTriggerType;
  }

  if (workflowSettings?.webhookTrigger?.enabled) return "webhook";
  if (workflowSettings?.leadstatsTrigger?.enabled) return "leadstats_event";
  return "audience_schedule";
};

const normalizeWebhookTrigger = (
  webhookTrigger?: WorkflowWebhookTrigger
): WorkflowWebhookTrigger => {
  const defaultRateLimit = defaultWorkflowSettings.webhookTrigger!.rateLimit!;
  return {
    ...defaultWorkflowSettings.webhookTrigger!,
    ...(webhookTrigger || {}),
    source: "webhook",
    identityPaths:
      webhookTrigger?.identityPaths?.length &&
      Array.isArray(webhookTrigger.identityPaths)
        ? webhookTrigger.identityPaths
        : defaultWorkflowSettings.webhookTrigger!.identityPaths,
    rateLimit: {
      enabled: webhookTrigger?.rateLimit?.enabled ?? defaultRateLimit.enabled,
      maxPerMinute:
        webhookTrigger?.rateLimit?.maxPerMinute ??
        defaultRateLimit.maxPerMinute,
      maxPerHour:
        webhookTrigger?.rateLimit?.maxPerHour ?? defaultRateLimit.maxPerHour,
    },
  };
};

const applyEntryTriggerType = (
  workflowSettings: WorkflowSettings,
  entryTriggerType: WorkflowEntryTriggerType
): WorkflowSettings => ({
  ...workflowSettings,
  entryTriggerType,
  leadstatsTrigger: workflowSettings.leadstatsTrigger
    ? {
        ...workflowSettings.leadstatsTrigger,
        enabled: entryTriggerType === "leadstats_event",
      }
    : workflowSettings.leadstatsTrigger,
  webhookTrigger: workflowSettings.webhookTrigger
    ? {
        ...workflowSettings.webhookTrigger,
        enabled: entryTriggerType === "webhook",
      }
    : workflowSettings.webhookTrigger,
});

const getEmptyActivationPreflight = (): WorkflowPreflightResult => ({
  ok: true,
  issueCount: 0,
  issues: [],
});

const clearActivationPreflight = (state: FlowBuilderState) => {
  state.activationPreflight = getEmptyActivationPreflight();
};

const initialState: FlowBuilderState = {
  flowId: "",
  flowName: "",
  nodes: getLayoutedNodes(initialNodes.slice(), initialEdges.slice()),
  edges: initialEdges.slice(),
  isDragging: false,
  stepperIndex: 0,
  segments: {
    type: SegmentsSettingsType.ALL_CUSTOMERS,
  },
  workflowType: WorkflowType.DYNAMIC,
  isViewMode: false,
  flowStatus: WorkflowStatus.DRAFT,
  showSegmentsErrors: false,
  isOnboarding: false,
  isOnboardingWaitUntilTooltipVisible: false,
  requireSaveEmit: false,
  sidePanelErrors: {},
  isOnboardingWaitUntilTimeSettingTooltipVisible: false,
  jumpToTargettingNode: undefined,
  isDrawerDisabled: false,
  devModeState: defaultDevMode,
  segmentQueryErrors: {},
  availableTags: [],
  workflowEntrySettings: defaultWorkflowEntrySettings,
  workflowSettings: defaultWorkflowSettings,
  isStarting: false,
  activationPreflight: getEmptyActivationPreflight(),
  dragDeleteHistory: [],
};

const handlePruneNodeTree = (state: FlowBuilderState, nodeId: string) => {
  const node = state.nodes.find((n) => n.id === nodeId);
  if (!node) return;
  const nodeIndex = state.nodes.indexOf(node);

  const children = getOutgoers(node, state.nodes, state.edges);

  state.edges = state.edges.filter(
    (edge) => edge.source !== node.id && edge.target !== node.id
  );
  state.nodes.splice(nodeIndex, 1);

  for (const child of children) {
    handlePruneNodeTree(state, child.id);
  }

  state.nodes = getLayoutedNodes(state.nodes, state.edges);
};

const handleAttachNodesToBranch = (state: FlowBuilderState, nodeId: string) => {
  // add existing following nodes to the first branch of the nodeId.
  // used to attach a node to an experiment branch when it's inserted between nodes.

  const node = state.nodes.find((n) => n.id === nodeId);

  if (!node) return;

  const nodeIndex = state.nodes.indexOf(node);

  const branchSource =
    "branches" in node.data ? node.data.branches?.[0]?.id : undefined; // get the id of the first branch of the node

  const targetNode = state.nodes[nodeIndex + 1]; //get the node that needs to be attached to the branch

  state.edges = state.edges.map((edge) => {
    //change experiment branch target to the existing node that goes to the branch
    if (branchSource && edge.id?.includes(branchSource) && targetNode.id) {
      return { ...edge, target: targetNode.id };
    }

    return edge;
  });
};

const handleRemoveNode = (state: FlowBuilderState, nodeId: string) => {
  const node = state.nodes.find((n) => n.id === nodeId);
  if (!node || node.type === NodeType.START) return;
  const nodeIndex = state.nodes.indexOf(node);

  const incomers = getIncomers(node, state.nodes, state.edges);
  const outgoers = getOutgoers(node, state.nodes, state.edges);

  if (incomers.length !== 1) {
    return;
  }

  if (outgoers.length > 1) {
    node.type = NodeType.EMPTY;
    node.data = {};

    for (const outgoer of outgoers) {
      handlePruneNodeTree(state, outgoer.id);
    }
    return;
  }

  const nodeIn = incomers[0];

  const parentIsBranching =
    nodeIn.type === NodeType.WAIT_UNTIL ||
    nodeIn.type === NodeType.MULTISPLIT ||
    nodeIn.type === NodeType.USER_ATTRIBUTE ||
    nodeIn.type === NodeType.EXPERIMENT ||
    nodeIn.type === NodeType.RESPONSE_BRANCH;

  // For a branch's child, deleting must leave a fresh "Next step" drop slot
  // wired to the branch — chaining through to whatever was below would
  // dead-end the branch when the next node is a terminal (e.g. Exit) and
  // leave the user with nowhere to drop a replacement step.
  if (parentIsBranching) {
    node.type = NodeType.EMPTY;
    node.data = {};
    state.edges = state.edges.filter((edge) => edge.source !== node.id);
    for (const outgoer of outgoers) {
      handlePruneNodeTree(state, outgoer.id);
    }
    state.nodes = getLayoutedNodes(state.nodes, state.edges);
    return;
  }

  let nodeOut = outgoers[0];
  if (!nodeOut) {
    nodeOut = {
      id: uuid(),
      type: NodeType.EMPTY,
      data: {},
      position: { x: 0, y: 0 },
    };
    state.nodes.push(nodeOut);
  }

  state.edges = state.edges.filter(
    (edge) => edge.source !== node.id && edge.target !== node.id
  );
  state.edges.push({
    id: `e${nodeIn.id}-${nodeOut.id}`,
    type: EdgeType.PRIMARY,
    source: nodeIn.id,
    target: nodeOut.id,
  });

  state.nodes.splice(nodeIndex, 1);

  state.nodes = getLayoutedNodes(state.nodes, state.edges);
};

const handleClearInsertNodes = (state: FlowBuilderState) => {
  for (const node of state.nodes) {
    if (node.type === NodeType.INSERT_NODE) {
      if (getOutgoers(node, state.nodes, state.edges).length === 0) {
        node.type = NodeType.EMPTY;
      } else {
        handleRemoveNode(state, node.id);
      }
    }
  }
};

const handleJumpToTargettingNodeChange = (
  state: FlowBuilderState,
  payload: string | undefined
) => {
  state.jumpToTargettingNode = payload;
  state.isDrawerDisabled = Boolean(payload);
  state.nodes = applyNodeChanges(
    state.nodes.map<NodeChange>((node) => ({
      type: "select",
      id: node.id,
      selected: false,
    })),
    state.nodes
  ).map((node) =>
    payload
      ? {
          ...node,
          data: {
            ...node.data,
            disabled: nodeTypesNotConnectableByJumpTo.includes(node.type),
          },
        }
      : { ...node, data: { ...node.data, disabled: false } }
  );
};

const flowBuilderSlice = createSlice({
  name: "flowBuilder",
  initialState,
  reducers: {
    setFlowId(state, action: PayloadAction<string>) {
      state.flowId = action.payload;
    },
    setFlowName(state, action: PayloadAction<string>) {
      state.flowName = action.payload;
    },
    setNodes(state, action: PayloadAction<Node<NodeData>[]>) {
      state.nodes = getLayoutedNodes(action.payload, state.edges);
    },
    loadVisualLayout(
      state,
      action: PayloadAction<{
        nodes: Node<NodeData>[];
        edges: Edge<EdgeData>[];
      }>
    ) {
      state.edges = action.payload.edges;
      // Re-run the layout on every load so saved-but-stale positions
      // (e.g. from a prior buggy layout pass) get corrected before the
      // canvas paints, instead of waiting for the next user edit.
      state.nodes = getLayoutedNodes(
        action.payload.nodes,
        action.payload.edges
      );
      clearActivationPreflight(state);
    },
    // Merge per-step statistics into the already-rendered nodes by stepId,
    // without re-running the layout. Lets the viewer paint immediately and
    // fill metrics in the background instead of blocking the canvas/panel on
    // slow stats queries.
    applyNodeStats(
      state,
      action: PayloadAction<{
        statsByStepId: Record<string, NodeData["stats"]>;
        countByStepId?: Record<string, number>;
      }>
    ) {
      const { statsByStepId, countByStepId } = action.payload;
      state.nodes = state.nodes.map((node) => {
        const stepId = (node.data as { stepId?: string })?.stepId;
        if (!stepId) return node;
        const stats = statsByStepId[stepId];
        const count = countByStepId?.[stepId];
        if (stats === undefined && count === undefined) return node;
        return {
          ...node,
          data: {
            ...node.data,
            ...(stats !== undefined ? { stats } : {}),
            ...(count !== undefined ? { customersCount: count } : {}),
          },
        };
      });
    },
    addInsertNodeBetween(
      state,
      action: PayloadAction<{ source: string; target: string }>
    ) {
      handleClearInsertNodes(state);

      const { source, target } = action.payload;

      const edgeBetween = state.edges.find(
        (edge) => edge.source === source && edge.target === target
      );
      if (!edgeBetween) return;

      const newNodeUUID = uuid();

      state.nodes.push({
        id: newNodeUUID,
        type: NodeType.INSERT_NODE,
        data: {},
        position: { x: 0, y: 0 },
      });

      state.edges.splice(state.edges.indexOf(edgeBetween), 1);
      state.edges.push(
        {
          id: `e${source}-${newNodeUUID}`,
          type: EdgeType.PRIMARY,
          source,
          target: newNodeUUID,
        },
        {
          id: `e${newNodeUUID}-${target}`,
          type: EdgeType.PRIMARY,
          source: newNodeUUID,
          target,
        }
      );

      state.nodes = getLayoutedNodes(state.nodes, state.edges);
    },
    transformEmptyNodeIntoInsertNode(state, action: PayloadAction<string>) {
      const nodeToChange = state.nodes.find(
        (node) => node.id === action.payload
      );

      if (!nodeToChange || nodeToChange.type !== NodeType.EMPTY) return;

      handleClearInsertNodes(state);

      nodeToChange.type = NodeType.INSERT_NODE;

      state.nodes = getLayoutedNodes(state.nodes, state.edges);
    },
    transformInsertNodeIntoEmptyNode(state, action: PayloadAction<string>) {
      const nodeToChange = state.nodes.find(
        (node) => node.id === action.payload
      );

      if (!nodeToChange || nodeToChange.type !== NodeType.INSERT_NODE) return;

      nodeToChange.type = NodeType.EMPTY;

      state.nodes = getLayoutedNodes(state.nodes, state.edges);
    },
    clearInsertNodes(state) {
      handleClearInsertNodes(state);
    },
    changeNodeData(
      state,
      action: PayloadAction<{ id: string; data: NodeData }>
    ) {
      const { id, data } = action.payload;

      const nodeToChange = state.nodes.find((node) => node.id === id);
      if (!nodeToChange) return;

      nodeToChange.data = data;
      clearActivationPreflight(state);

      if (
        (nodeToChange.type === NodeType.WAIT_UNTIL &&
          nodeToChange.data.type === NodeType.WAIT_UNTIL) ||
        (nodeToChange.type === NodeType.MULTISPLIT &&
          nodeToChange.data.type === NodeType.MULTISPLIT) ||
        (nodeToChange.type === NodeType.EXPERIMENT &&
          nodeToChange.data.type === NodeType.EXPERIMENT) ||
        (nodeToChange.type === NodeType.RESPONSE_BRANCH &&
          nodeToChange.data.type === NodeType.RESPONSE_BRANCH)
      ) {
        const existedBranchEdges = state.edges.filter(
          (edge) => edge.source === nodeToChange.id
        );

        if (
          nodeToChange.type === NodeType.MULTISPLIT &&
          nodeToChange.data.type === NodeType.MULTISPLIT
        ) {
          if (
            !nodeToChange.data.branches.some((el) => el.isOthers) &&
            nodeToChange.data.branches.length > 0
          ) {
            nodeToChange.data.branches.push({
              id: uuid(),
              type: BranchType.MULTISPLIT,
              isOthers: true,
            });
          } else if (nodeToChange.data.branches.some((el) => el.isOthers)) {
            const otherNodeIndex = nodeToChange.data.branches.findIndex(
              (el) => el.isOthers
            );
            if (nodeToChange.data.branches.length - 1 > 0) {
              if (otherNodeIndex !== nodeToChange.data.branches.length - 1) {
                const element = nodeToChange.data.branches.splice(
                  otherNodeIndex,
                  1
                )[0];
                nodeToChange.data.branches.push(element);
              }
            }
          }
        }

        if (
          nodeToChange.type === NodeType.RESPONSE_BRANCH &&
          nodeToChange.data.type === NodeType.RESPONSE_BRANCH
        ) {
          // Always keep an isOthers branch present, ordered last.
          if (
            !nodeToChange.data.branches.some((el) => el.isOthers) &&
            nodeToChange.data.branches.length > 0
          ) {
            nodeToChange.data.branches.push({
              id: uuid(),
              type: BranchType.RESPONSE,
              isOthers: true,
              label: "Any other reply",
              matchType: "keyword",
              matchValue: "",
            });
          }
          // Move isOthers to end (after isTimeout if present).
          const branches = nodeToChange.data.branches;
          const otherIdx = branches.findIndex((el) => el.isOthers);
          if (otherIdx >= 0 && otherIdx !== branches.length - 1) {
            const el = branches.splice(otherIdx, 1)[0];
            branches.push(el);
          }
        }

        // prune disconnected branches
        for (const edge of existedBranchEdges) {
          if (
            (!edge.data ||
              edge.type !== EdgeType.BRANCH ||
              edge.data.type !== EdgeType.BRANCH ||
              !(nodeToChange.data.branches as Branch[]).find(
                (branch) =>
                  branch.id === (edge as Edge<BranchEdgeData>).data?.branch.id
              )) &&
            nodeToChange.type !== NodeType.EXPERIMENT
          ) {
            handlePruneNodeTree(state, edge.target);
          }
        }

        //connect new and update old branches
        for (const branch of nodeToChange.data.branches) {
          const existedChildrenEdge = existedBranchEdges.find(
            (edge) =>
              edge.data?.type === EdgeType.BRANCH &&
              edge.data.branch.id === branch.id
          );

          if (!existedChildrenEdge) {
            const newEmptyNodeUUID = uuid();
            const newNode = {
              id: newEmptyNodeUUID,
              type: NodeType.EMPTY,
              data: {},
              position: { x: 0, y: 0 },
            };

            state.nodes.push(newNode);

            const newEdge = {
              id: `b${branch.id}`,
              type: EdgeType.BRANCH,
              data: {
                type: EdgeType.BRANCH,
                branch,
              },
              source: nodeToChange.id,
              target: newEmptyNodeUUID,
            };

            state.edges.push(newEdge);

            continue;
          }

          existedChildrenEdge.data = { type: EdgeType.BRANCH, branch };
        }

        if (NodeType.EXPERIMENT === nodeToChange.type) {
          handleAttachNodesToBranch(state, nodeToChange.id);
        }

        if (
          state.devModeState.status === ConnectionStatus.Connected &&
          !state.nodes.find((el) => el.id === state.devModeState.contactInNode)
        ) {
          const start = state.nodes.find((el) => el.type === NodeType.START);
          state.devModeState.requireMovementToStart = start?.id;
        }
        state.nodes = getLayoutedNodes(state.nodes, state.edges);
      }
    },
    removeNode(state, action: PayloadAction<string>) {
      handleRemoveNode(state, action.payload);
      clearActivationPreflight(state);

      if (state.devModeState.status === ConnectionStatus.Connected) {
        if (
          !state.nodes.find(
            (el) => el.id === state.devModeState.arrowPreSelectNode
          )
        ) {
          state.devModeState.arrowPreSelectNode = undefined;
        }
        if (
          !state.nodes.find((el) => el.id === state.devModeState.contactInNode)
        ) {
          const start = state.nodes.find((el) => el.type === NodeType.START);
          state.devModeState.requireMovementToStart = start?.id;
        }
      }
    },
    removeNodeWithUndo(state, action: PayloadAction<string>) {
      const target = state.nodes.find((n) => n.id === action.payload);
      if (!target || target.type === NodeType.START) {
        return;
      }

      const snapshotNodes: Node<NodeData>[] = JSON.parse(
        JSON.stringify(state.nodes)
      );
      const snapshotEdges: Edge<EdgeData>[] = JSON.parse(
        JSON.stringify(state.edges)
      );

      handleRemoveNode(state, action.payload);

      // handleRemoveNode bails (e.g. when the node has multiple incomers and
      // the graph would become invalid) without touching state. If nothing
      // changed, there is nothing meaningful to undo.
      const stillUntouched =
        state.nodes.length === snapshotNodes.length &&
        state.edges.length === snapshotEdges.length &&
        state.nodes.every(
          (n, i) =>
            n.id === snapshotNodes[i].id && n.type === snapshotNodes[i].type
        );

      if (stillUntouched) {
        return;
      }

      clearActivationPreflight(state);
      state.dragDeleteHistory.push({
        nodes: snapshotNodes,
        edges: snapshotEdges,
      });
      // Cap session-long history so a long editing run can't grow the
      // store unbounded. 50 is plenty for "I deleted too many things"
      // recovery; older snapshots are dropped from the front.
      if (state.dragDeleteHistory.length > 50) {
        state.dragDeleteHistory.shift();
      }

      if (state.devModeState.status === ConnectionStatus.Connected) {
        if (
          !state.nodes.find(
            (el) => el.id === state.devModeState.arrowPreSelectNode
          )
        ) {
          state.devModeState.arrowPreSelectNode = undefined;
        }
        if (
          !state.nodes.find((el) => el.id === state.devModeState.contactInNode)
        ) {
          const start = state.nodes.find((el) => el.type === NodeType.START);
          state.devModeState.requireMovementToStart = start?.id;
        }
      }
    },
    undoNodeRemoval(state) {
      const snap = state.dragDeleteHistory.pop();
      if (!snap) return;
      state.nodes = snap.nodes;
      state.edges = snap.edges;
      clearActivationPreflight(state);
    },
    clearNodeRemovalHistory(state) {
      state.dragDeleteHistory = [];
    },
    pruneNodeTree(state, action: PayloadAction<string>) {
      handlePruneNodeTree(state, action.payload);
      clearActivationPreflight(state);
    },
    setEdges(state, action: PayloadAction<Edge<EdgeData>[]>) {
      state.edges = action.payload;
      state.nodes = getLayoutedNodes(state.nodes, state.edges);
    },
    handleDevModeState(state, action: PayloadAction<DevModeStatePayload>) {
      if ("contactInNode" in action.payload) {
        const node = state.nodes.find(
          (el) => el.id === action.payload.contactInNode
        );
        if (node) {
          const availableNodes = getClosestNextAndPrevious(
            node,
            state.nodes,
            state.edges
          ).filter((el) => el.type !== NodeType.EMPTY);

          const start = state.nodes.find((el) => el.type === NodeType.START);

          if (node.type === NodeType.EXIT && start) {
            availableNodes.push(start);
          }

          action.payload.availableNodeToJump = availableNodes.map(
            (el) => el.id
          );

          state.devModeState.arrowPreSelectNode = undefined;
        }
      }

      state.devModeState = {
        ...state.devModeState,
        ...action.payload,
      };
    },
    recountAvailableNodes(state) {
      if (
        state.devModeState.status !== ConnectionStatus.Connected ||
        !state.devModeState.contactInNode
      )
        return;

      const node = state.nodes.find(
        (el) => el.id === state.devModeState.contactInNode
      );
      if (!node) return;

      const availableNodes = getClosestNextAndPrevious(
        node,
        state.nodes,
        state.edges
      ).filter((el) => el.type !== NodeType.EMPTY);

      const start = state.nodes.find((el) => el.type === NodeType.START);

      if (node.type === NodeType.EXIT && start) {
        availableNodes.push(start);
      }

      state.devModeState.availableNodeToJump = availableNodes.map(
        (el) => el.id
      );

      state.devModeState.arrowPreSelectNode = undefined;
    },
    resetDevMode(state) {
      state.devModeState = defaultDevMode;
    },
    handleDrawerAction(
      state,
      action: PayloadAction<{ id: string; action: string; stepId?: string }>
    ) {
      const { stepId } = action.payload;

      const nodeToChange = state.nodes.find(
        (node) => node.id === action.payload.id
      );

      if (!nodeToChange) return;

      clearActivationPreflight(state);

      let newMessageNodeName = "";

      const messageNodes = state.nodes.filter((node) => node.type == "message");
      let postFixValue = messageNodes.length;
      newMessageNodeName = `${capitalize(action.payload.action)} ${
        postFixValue + 1
      }`;

      while (
        messageNodes.find(
          /* eslint-disable */
          (node) =>
            (node?.data as MessageNodeData)?.customName === newMessageNodeName
        )
      ) {
        postFixValue++;
        newMessageNodeName = `${capitalize(
          action.payload.action
        )} ${postFixValue}`;
      }

      switch (action.payload.action) {
        case DrawerAction.EMAIL:
          nodeToChange.type = NodeType.MESSAGE;
          nodeToChange.data = {
            type: NodeType.MESSAGE,
            template: { type: MessageType.EMAIL },
            oneClickUnsubscribeEnabled: false,
            customName: newMessageNodeName,
            stepId,
          };
          break;
        case DrawerAction.INTERNAL_EMAIL:
          nodeToChange.type = NodeType.MESSAGE;
          nodeToChange.data = {
            type: NodeType.MESSAGE,
            template: { type: MessageType.EMAIL },
            recipientMode: "internal",
            internalRecipientGroupIds: [],
            additionalEmails: [],
            dynamicRecipientSources: [],
            oneClickUnsubscribeEnabled: false,
            customName: `Internal email ${postFixValue + 1}`,
            stepId,
          };
          break;
        case DrawerAction.SMS:
          nodeToChange.type = NodeType.MESSAGE;
          nodeToChange.data = {
            type: NodeType.MESSAGE,
            template: { type: MessageType.SMS },
            oneClickUnsubscribeEnabled: false,
            customName: newMessageNodeName,
            stepId,
          };
          break;
        case DrawerAction.SLACK:
          nodeToChange.type = NodeType.MESSAGE;
          nodeToChange.data = {
            type: NodeType.MESSAGE,
            template: { type: MessageType.SLACK },
            oneClickUnsubscribeEnabled: false,
            customName: newMessageNodeName,
            stepId,
          };
          break;
        case DrawerAction.PUSH:
          nodeToChange.type = NodeType.PUSH;
          nodeToChange.data = {
            type: NodeType.PUSH,
            template: { type: MessageType.PUSH },
            oneClickUnsubscribeEnabled: false,
            customName: newMessageNodeName,
            stepId,
          };
          break;
        case DrawerAction.WEBHOOK:
          nodeToChange.type = NodeType.MESSAGE;
          nodeToChange.data = {
            type: NodeType.MESSAGE,
            template: { type: MessageType.WEBHOOK },
            oneClickUnsubscribeEnabled: false,
            stepId,
          };
          break;
        case DrawerAction.IN_APP_MESSAGE:
          nodeToChange.type = NodeType.MESSAGE;
          nodeToChange.data = {
            type: NodeType.MESSAGE,
            template: { type: MessageType.IN_APP },
            oneClickUnsubscribeEnabled: false,
            stepId,
          };
          break;
        case DrawerAction.TRACKER:
          nodeToChange.type = NodeType.TRACKER;
          nodeToChange.data = {
            type: NodeType.TRACKER,
            stepId,
          };
          break;
        case DrawerAction.JUMP_TO:
          nodeToChange.type = NodeType.JUMP_TO;
          nodeToChange.data = {
            type: NodeType.JUMP_TO,
            stepId,
          };
          handleJumpToTargettingNodeChange(state, nodeToChange.id);
          break;
        case DrawerAction.EXIT:
          nodeToChange.type = NodeType.EXIT;
          nodeToChange.data = {
            stepId,
          };
          break;
        case DrawerAction.WAIT_UNTIL:
          nodeToChange.type = NodeType.WAIT_UNTIL;
          nodeToChange.data = {
            type: NodeType.WAIT_UNTIL,
            branches: [
              // {
              //   id: uuid(),
              //   type: BranchType.EVENT,
              //   conditions: [
              //     {
              //       name: "",
              //       providerType: ProviderType.CUSTOM,
              //       statements: [],
              //       relationToNext: LogicRelation.OR,
              //     },
              //   ],
              // },
            ],
            stepId,
          };
          break;
        case DrawerAction.TIME_DELAY:
          nodeToChange.type = NodeType.TIME_DELAY;
          nodeToChange.data = {
            type: NodeType.TIME_DELAY,
            delay: {
              days: 0,
              hours: 0,
              minutes: 0,
            },
            stepId,
          };
          break;
        case DrawerAction.TIME_WINDOW:
          nodeToChange.type = NodeType.TIME_WINDOW;
          nodeToChange.data = {
            type: NodeType.TIME_WINDOW,
            from: undefined,
            to: undefined,
            fromTime: "12:00",
            toTime: "23:59",
            onDays: [...new Array(7)].map(() => 0),
            windowType: TimeWindowTypes.SPEC_DATES,
            stepId,
          };
          break;
        case DrawerAction.MULTISPLIT:
          nodeToChange.type = NodeType.MULTISPLIT;
          nodeToChange.data = {
            type: NodeType.MULTISPLIT,
            stepId,
            branches: [],
          };
          break;
        case DrawerAction.EXPERIMENT:
          nodeToChange.type = NodeType.EXPERIMENT;
          nodeToChange.data = {
            type: NodeType.EXPERIMENT,
            stepId,
            branches: [
              {
                id: uuid(),
                type: BranchType.EXPERIMENT,
                ratio: 0.5,
                label: "Variant A",
              },
              {
                id: uuid(),
                type: BranchType.EXPERIMENT,
                ratio: 0.5,
                label: "Variant B",
              },
            ],
            winnerBranchId: null,
            autoWinner: {
              enabled: false,
              metric: "positive_rate",
              minAssignments: 100,
              targetPositive: 100,
            },
          };
          break;
        case DrawerAction.AI_RESPONSE:
          nodeToChange.type = NodeType.AI_RESPONSE;
          nodeToChange.data = {
            type: NodeType.AI_RESPONSE,
            stepId,
            channel: undefined,
            connectionId: undefined,
            subject: undefined,
            prompt: "",
            agentId: undefined,
            maxTurns: 10,
            replyTimeoutMinutes: 60 * 24,
            waitForCustomerReplyBeforeFirstResponse: false,
            replyOnly: false,
            onComplete: "advance",
          } as any;
          break;
        case DrawerAction.RESPONSE_BRANCH:
          nodeToChange.type = NodeType.RESPONSE_BRANCH;
          nodeToChange.data = {
            type: NodeType.RESPONSE_BRANCH,
            stepId,
            branches: [],
            routingMode: "ai",
            aiFallback: true,
          } as any;
          break;
        case DrawerAction.RICOCHET:
          nodeToChange.type = NodeType.RICOCHET;
          nodeToChange.data = {
            type: NodeType.RICOCHET,
            stepId,
            action: "updateStatus",
            statusId: undefined,
            statusName: undefined,
            tagName: "",
            tagNames: [],
            taskId: "",
            taskName: "",
            taskDescription: "",
            taskPriority: "4",
            taskInvitees: "",
            taskReminderDate: "",
            taskReminderTime: "",
            taskReminderPopup: false,
            taskReminderEmail: false,
            note: "",
          };
          break;
        default:
          break;
      }
      if (NodeType.MULTISPLIT === nodeToChange.type) {
        // filter all nodes that are multisplit or will become multisplit and empty
        const branchNodes = state.nodes.filter(
          (node) =>
            node.type === NodeType.EMPTY || node.type === NodeType.MULTISPLIT
        );

        // find the first edge that is not a multisplit branch and should become multisplit branch
        const edgeToChange = state.edges.find((edge) => {
          const correspondingNode = branchNodes.find((node) => {
            return edge.source === node.id && edge.type !== EdgeType.BRANCH;
          });
          return correspondingNode;
        });

        state.edges = state.edges.map((edge) => {
          if (edge.id === edgeToChange?.id) {
            return {
              ...edge,
              type: EdgeType.BRANCH,
              data: {
                type: EdgeType.BRANCH,
                branch: {
                  id: edge.id,
                  type: BranchType.MULTISPLIT,
                  isOthers: true,
                },
              },
            };
          } else {
            return edge;
          }
        });
      }

      if (NodeType.WAIT_UNTIL === nodeToChange.type) {
        // filter all nodes that are wait until or will become wait until and empty
        const branchNodes = state.nodes.filter(
          (node) =>
            node.type === NodeType.EMPTY || node.type === NodeType.WAIT_UNTIL
        );

        // find the first edge that is not a wait until branch and should become wait until branch
        const edgeToChange = state.edges.find((edge) => {
          const correspondingNode = branchNodes.find((node) => {
            return edge.source === node.id && edge.type !== EdgeType.BRANCH;
          });
          return correspondingNode;
        });

        state.edges = state.edges.map((edge) => {
          if (edge.id === edgeToChange?.id) {
            return {
              ...edge,
              type: EdgeType.BRANCH,
              data: {
                type: EdgeType.BRANCH,
                branch: {
                  id: edge.id,
                  type: BranchType.EVENT,
                  conditions: [
                    {
                      type: EventType.ANALYTICS,
                      name: "",
                      providerType: ProviderType.CUSTOM,
                      relationToNext: LogicRelation.OR,
                      statements: [],
                    },
                  ],
                },
              },
            };
          } else {
            return edge;
          }
        });

        //add a default branch to the node
        state.nodes = state.nodes.map((node) => {
          if (node.id === nodeToChange?.id) {
            return {
              ...node,
              data: {
                ...node.data,
                type: NodeType.WAIT_UNTIL,
                branches: [
                  {
                    id:
                      state.edges.find(
                        (edge) =>
                          edge.id.includes(nodeToChange.id) &&
                          edge.type === EdgeType.BRANCH
                      )?.id || uuid(),
                    type: BranchType.EVENT,
                    conditions: [
                      {
                        type: EventType.ANALYTICS,
                        name: "",
                        providerType: ProviderType.CUSTOM,
                        relationToNext: LogicRelation.OR,
                        statements: [],
                      },
                    ],
                  },
                ],
              },
            };
          } else {
            return node;
          }
        });
      }

      if (NodeType.RESPONSE_BRANCH === nodeToChange.type) {
        const branchNodes = state.nodes.filter(
          (node) =>
            node.type === NodeType.EMPTY ||
            node.type === NodeType.RESPONSE_BRANCH
        );

        const edgeToChange = state.edges.find((edge) => {
          const correspondingNode = branchNodes.find((node) => {
            return edge.source === node.id && edge.type !== EdgeType.BRANCH;
          });
          return correspondingNode;
        });

        state.edges = state.edges.map((edge) => {
          if (edge.id === edgeToChange?.id) {
            return {
              ...edge,
              type: EdgeType.BRANCH,
              data: {
                type: EdgeType.BRANCH,
                branch: {
                  id: edge.id,
                  type: BranchType.RESPONSE,
                  isOthers: true,
                  label: "Any other reply",
                  matchType: "keyword",
                  matchValue: "",
                },
              },
            };
          } else {
            return edge;
          }
        });
      }

      if (
        !state.edges.some((edge) => edge.source === nodeToChange.id) &&
        !(
          [
            NodeType.JUMP_TO,
            NodeType.WAIT_UNTIL,
            NodeType.USER_ATTRIBUTE,
            NodeType.EXIT,
            NodeType.MULTISPLIT,
            NodeType.EXPERIMENT,
            NodeType.RESPONSE_BRANCH,
          ] as string[]
        ).includes(nodeToChange.type || "")
      ) {
        const newNodeId = uuid();
        state.nodes.push({
          id: newNodeId,
          type: NodeType.EMPTY,
          data: {},
          position: {
            x: 0,
            y: 0,
          },
        });

        state.edges.push({
          id: `${nodeToChange.id}-${newNodeId}`,
          type: EdgeType.PRIMARY,
          source: nodeToChange.id,
          target: newNodeId,
        });
      }

      state.nodes = applyNodeChanges(
        [
          ...state.nodes.map<NodeChange>((node) => ({
            type: "select",
            id: node.id,
            selected: false,
          })),
          { type: "select", id: nodeToChange.id, selected: true },
        ],
        state.nodes
      );

      state.nodes = getLayoutedNodes(state.nodes, state.edges);
    },
    selectNode(state, action: PayloadAction<string>) {
      state.nodes = applyNodeChanges(
        [
          ...state.nodes.map<NodeChange>((node) => ({
            type: "select",
            id: node.id,
            selected: false,
          })),
          { type: "select", id: action.payload, selected: true },
        ],
        state.nodes
      );
    },
    deselectNodes(state) {
      state.nodes = applyNodeChanges(
        state.nodes.map<NodeChange>((node) => ({
          type: "select",
          id: node.id,
          selected: false,
        })),
        state.nodes
      );
    },
    setIsDragging(state, action: PayloadAction<boolean>) {
      state.isDragging = action.payload;
    },
    setDragAction(state, action: PayloadAction<DragAction | undefined>) {
      state.dragAction = action.payload;
    },
    setStepperIndex(state, action: PayloadAction<0 | 1 | 2 | 3>) {
      state.stepperIndex = action.payload;
    },
    setSegmentsSettings(state, action: PayloadAction<SegmentsSettings>) {
      state.segments = action.payload;
      clearActivationPreflight(state);
    },
    setWorkflowType(state, action: PayloadAction<WorkflowType>) {
      state.workflowType = action.payload;
      clearActivationPreflight(state);
    },
    setIsViewMode(state, action: PayloadAction<boolean>) {
      state.isViewMode = action.payload;
    },
    setFlowStatus(state, action: PayloadAction<WorkflowStatus>) {
      state.flowStatus = action.payload;
    },
    setAvailableTags(state, action: PayloadAction<string[]>) {
      state.availableTags = action.payload;
    },
    setShowSegmentsErrors(state, action: PayloadAction<boolean>) {
      if (!Object.keys(state.segmentQueryErrors).length)
        state.showSegmentsErrors = false;
      else state.showSegmentsErrors = action.payload;
    },
    setIsOnboarding(state, action: PayloadAction<boolean>) {
      state.isOnboarding = action.payload;
    },
    setRequireSaveEmit(state, action: PayloadAction<boolean>) {
      state.requireSaveEmit = action.payload;
    },
    addSidePanelError(state, action: PayloadAction<string>) {
      state.sidePanelErrors[action.payload] = true;
    },
    removeSidePanelError(state, action: PayloadAction<string>) {
      delete state.sidePanelErrors[action.payload];
    },
    setWorkflowSettingsTags(state, action: PayloadAction<string[]>) {
      state.workflowSettings.tags = Array.from(new Set(action.payload));
      clearActivationPreflight(state);
    },
    setWorkflowSettingsMaxEntries(
      state,
      action: PayloadAction<WorkflowSettingsMaxUserEntries>
    ) {
      state.workflowSettings.maxEntries = action.payload;
      clearActivationPreflight(state);
    },
    setWorkflowSettingsQuietHours(
      state,
      action: PayloadAction<WorkflowSettingsQuietHours>
    ) {
      state.workflowSettings.quietHours = action.payload;
      clearActivationPreflight(state);
    },
    setWorkflowFrequencyCappingRules(
      state,
      action: PayloadAction<WorkflowSettingsEnableFrequencyCapping>
    ) {
      state.workflowSettings.frequencyCapping = action.payload;
      clearActivationPreflight(state);
    },

    setMaxMessageSends(
      state,
      action: PayloadAction<WorkflowSettingsMaxMessageSends>
    ) {
      state.workflowSettings.maxMessageSends = action.payload;
      clearActivationPreflight(state);
    },
    clearSidePanelError(state) {
      state.sidePanelErrors = {};
    },
    setWorkflowEntryTimingType(state, action: PayloadAction<EntryTiming>) {
      state.workflowEntrySettings.entryTiming.type = action.payload;
      if (action.payload === EntryTiming.WhenPublished) {
        state.workflowEntrySettings.entryTiming.time = undefined;
      } else {
        state.workflowEntrySettings.entryTiming.time = {
          frequency: EntryTimingFrequency.Once,
          startDate: new Date().toISOString(),
          recurrence: {
            endsOn: RecurrenceEndsOption.Never,
            repeatEvery: 1,
            weeklyOn: [...new Array(7)].map(() => 0),
            endAdditionalValue: undefined,
          },
          userLocalTimeZone: false,
        };
      }
      clearActivationPreflight(state);
    },
    setWorkflowEntryTimingTime(
      state,
      action: PayloadAction<EntryTimingSpecificTime>
    ) {
      let weeklyOn = null;
      let defaultAdditionalValue: number | string | undefined | null = null;
      if (state.workflowEntrySettings.entryTiming?.time) {
        if (
          state.workflowEntrySettings.entryTiming?.time.frequency !==
            action.payload.frequency &&
          action.payload.frequency === EntryTimingFrequency.Weekly
        ) {
          const now = getDay(new Date());
          weeklyOn = [...new Array(7)].map(() => 0);
          weeklyOn[now === 0 ? 6 : now - 1] = 1;
        }
        if (
          state.workflowEntrySettings.entryTiming?.time.recurrence.endsOn !==
          action.payload.recurrence.endsOn
        ) {
          if (action.payload.recurrence.endsOn === RecurrenceEndsOption.After)
            defaultAdditionalValue = 1;
          else if (
            action.payload.recurrence.endsOn ===
            RecurrenceEndsOption.SpecificDate
          )
            defaultAdditionalValue = addDays(new Date(), 5).toISOString();
          else defaultAdditionalValue = undefined;
        }
      }

      state.workflowEntrySettings.entryTiming.time = action.payload;

      if (weeklyOn) {
        state.workflowEntrySettings.entryTiming.time = {
          ...state.workflowEntrySettings.entryTiming?.time,
          recurrence: {
            ...state.workflowEntrySettings.entryTiming?.time.recurrence,
            weeklyOn: weeklyOn,
          },
        };
      }

      if (defaultAdditionalValue !== null) {
        state.workflowEntrySettings.entryTiming.time = {
          ...state.workflowEntrySettings.entryTiming?.time,
          recurrence: {
            ...state.workflowEntrySettings.entryTiming?.time.recurrence,
            endAdditionalValue: defaultAdditionalValue,
          },
        };
      }
      clearActivationPreflight(state);
    },
    setWorkflowEntryEnrollmentType(
      state,
      action: PayloadAction<WorkflowEnrollmentType>
    ) {
      state.workflowEntrySettings.enrollmentType = action.payload;
      clearActivationPreflight(state);
    },
    setWorkflowEntrySettings(
      state,
      action: PayloadAction<WorkflowEntrySettings | undefined>
    ) {
      if (action.payload === undefined)
        state.workflowEntrySettings = defaultWorkflowEntrySettings;
      else state.workflowEntrySettings = action.payload;
      clearActivationPreflight(state);
    },
    setWorkflowSettings(
      state,
      action: PayloadAction<WorkflowSettings | undefined>
    ) {
      if (action.payload === undefined)
        state.workflowSettings = defaultWorkflowSettings;
      else {
        const entryTriggerType = getWorkflowEntryTriggerType(action.payload);
        const workflowSettings = {
          ...defaultWorkflowSettings,
          ...action.payload,
          entryTriggerType,
          maxMessageSends: {
            ...defaultWorkflowSettings.maxMessageSends,
            ...action.payload.maxMessageSends,
          },
          quietHours: {
            ...defaultWorkflowSettings.quietHours,
            ...action.payload.quietHours,
          },
          leadstatsTrigger:
            action.payload.leadstatsTrigger ??
            defaultWorkflowSettings.leadstatsTrigger,
          webhookTrigger: normalizeWebhookTrigger(
            action.payload.webhookTrigger
          ),
        };
        state.workflowSettings = applyEntryTriggerType(
          workflowSettings,
          entryTriggerType
        );
      }
      clearActivationPreflight(state);
    },
    setWorkflowEntryTriggerType(
      state,
      action: PayloadAction<WorkflowEntryTriggerType>
    ) {
      state.workflowSettings = applyEntryTriggerType(
        {
          ...state.workflowSettings,
          leadstatsTrigger:
            state.workflowSettings.leadstatsTrigger ??
            defaultWorkflowSettings.leadstatsTrigger,
          webhookTrigger: normalizeWebhookTrigger(
            state.workflowSettings.webhookTrigger
          ),
        },
        action.payload
      );
      clearActivationPreflight(state);
    },
    setLeadStatsWorkflowTrigger(
      state,
      action: PayloadAction<LeadStatsWorkflowTrigger>
    ) {
      state.workflowSettings.leadstatsTrigger = action.payload;
      if (action.payload.enabled) {
        state.workflowSettings.entryTriggerType = "leadstats_event";
        if (state.workflowSettings.webhookTrigger) {
          state.workflowSettings.webhookTrigger.enabled = false;
        }
      } else if (
        state.workflowSettings.entryTriggerType === "leadstats_event"
      ) {
        state.workflowSettings.entryTriggerType = "audience_schedule";
      }
      clearActivationPreflight(state);
    },
    setWebhookWorkflowTrigger(
      state,
      action: PayloadAction<WorkflowWebhookTrigger>
    ) {
      state.workflowSettings.webhookTrigger = normalizeWebhookTrigger(
        action.payload
      );
      if (action.payload.enabled) {
        state.workflowSettings.entryTriggerType = "webhook";
        if (state.workflowSettings.leadstatsTrigger) {
          state.workflowSettings.leadstatsTrigger.enabled = false;
        }
      } else if (state.workflowSettings.entryTriggerType === "webhook") {
        state.workflowSettings.entryTriggerType = "audience_schedule";
      }
      clearActivationPreflight(state);
    },
    addSegmentQueryError(state, action: PayloadAction<string>) {
      state.segmentQueryErrors[action.payload] = true;
    },
    removeSegmentQueryError(state, action: PayloadAction<string>) {
      delete state.segmentQueryErrors[action.payload];
    },
    clearSegmentPanelErrors(state) {
      state.segmentQueryErrors = {};
    },
    setIsOnboardingWaitUntilTooltipVisible(
      state,
      action: PayloadAction<boolean>
    ) {
      state.isOnboardingWaitUntilTooltipVisible = action.payload;
    },
    setIsOnboardingWaitUntilTimeSettingTooltipVisible(
      state,
      action: PayloadAction<boolean>
    ) {
      state.isOnboardingWaitUntilTimeSettingTooltipVisible = action.payload;
    },
    setJumpToTargettingNode(state, action: PayloadAction<string | undefined>) {
      handleJumpToTargettingNodeChange(state, action.payload);
    },
    setIsDrawerDisabled(state, action: PayloadAction<boolean>) {
      state.isDrawerDisabled = action.payload;
    },
    setTemplateInlineCreator(
      state,
      action: PayloadAction<TemplateInlineEditor | undefined>
    ) {
      state.templateInlineCreation = action.payload;
    },
    refreshFlowBuilder(state) {
      state.flowId = "";
      state.flowName = "";
      state.nodes = initialNodes.slice();
      state.edges = initialEdges.slice();
      state.isDragging = false;
      state.dragAction = undefined;
      state.stepperIndex = 0;
      state.segments = { type: SegmentsSettingsType.ALL_CUSTOMERS };
      state.workflowType = WorkflowType.DYNAMIC;
      state.isViewMode = false;
      state.flowStatus = WorkflowStatus.DRAFT;
      state.showSegmentsErrors = false;
      state.isOnboarding = false;
      state.isOnboardingWaitUntilTooltipVisible = false;
      state.jumpToTargettingNode = undefined;
      state.isDrawerDisabled = false;
      clearActivationPreflight(state);
    },
    setIsStarting(state, action: PayloadAction<boolean>) {
      state.isStarting = action.payload;
    },
    setActivationPreflightResult(
      state,
      action: PayloadAction<WorkflowPreflightResult>
    ) {
      const issues = action.payload.issues || [];
      state.activationPreflight = {
        ok: action.payload.ok,
        issues,
        issueCount: action.payload.issueCount ?? issues.length,
      };
    },
    clearActivationPreflightIssues(state) {
      clearActivationPreflight(state);
    },
    setWorkflowSettingsConversionTracking(
      state,
      action: PayloadAction<WorkflowSettingsConversionTracking>
    ) {
      if (action.payload === undefined)
        state.workflowSettings.conversionTracking =
          defaultWorkflowSettings.conversionTracking;
      else state.workflowSettings.conversionTracking = action.payload;

      state.workflowSettings.conversionTracking.events = Array.from(
        new Set(state.workflowSettings.conversionTracking.events)
      );
      clearActivationPreflight(state);
    },
    setWorkflowSettingsStrictLiquidChecking(
      state,
      action: PayloadAction<WorkflowSettingsStrictLiquidChecking>
    ) {
      if (action.payload === undefined)
        state.workflowSettings.strictLiquidChecking =
          defaultWorkflowSettings.strictLiquidChecking;
      else state.workflowSettings.strictLiquidChecking = action.payload;
      clearActivationPreflight(state);
    },
  },
});

export const {
  setFlowId,
  setFlowName,
  setNodes,
  loadVisualLayout,
  applyNodeStats,
  addInsertNodeBetween,
  transformEmptyNodeIntoInsertNode,
  transformInsertNodeIntoEmptyNode,
  clearInsertNodes,
  changeNodeData,
  removeNode,
  removeNodeWithUndo,
  undoNodeRemoval,
  clearNodeRemovalHistory,
  pruneNodeTree,
  setEdges,
  handleDrawerAction,
  selectNode,
  deselectNodes,
  setIsDragging,
  setDragAction,
  setStepperIndex,
  setSegmentsSettings,
  setWorkflowType,
  setIsViewMode,
  setFlowStatus,
  setShowSegmentsErrors,
  handleDevModeState,
  resetDevMode,
  setIsOnboarding,
  setIsOnboardingWaitUntilTooltipVisible,
  setIsOnboardingWaitUntilTimeSettingTooltipVisible,
  setJumpToTargettingNode,
  setIsDrawerDisabled,
  refreshFlowBuilder,
  setRequireSaveEmit,
  addSidePanelError,
  removeSidePanelError,
  clearSidePanelError,
  recountAvailableNodes,
  addSegmentQueryError,
  removeSegmentQueryError,
  clearSegmentPanelErrors,
  setWorkflowEntryTimingType,
  setWorkflowEntryTimingTime,
  setWorkflowEntryEnrollmentType,
  setWorkflowSettingsTags,
  setWorkflowEntrySettings,
  setWorkflowSettings,
  setWorkflowEntryTriggerType,
  setLeadStatsWorkflowTrigger,
  setWebhookWorkflowTrigger,
  setWorkflowSettingsMaxEntries,
  setWorkflowSettingsQuietHours,
  setMaxMessageSends,
  setAvailableTags,
  setTemplateInlineCreator,
  setWorkflowFrequencyCappingRules,
  setIsStarting,
  setActivationPreflightResult,
  clearActivationPreflightIssues,
  setWorkflowSettingsConversionTracking,
  setWorkflowSettingsStrictLiquidChecking,
} = flowBuilderSlice.actions;

export { defaultDevMode };

export default flowBuilderSlice.reducer;
